export interface PositionTotals {
  zeilen_netto: number;
  zeilen_steuer: number;
  zeilen_brutto: number;
}

export interface DocumentTotals {
  summe_netto: number;
  summe_steuer: number;
  summe_brutto: number;
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export type RabattModus = "prozent" | "betrag";
export type RabattAusweis = "intern" | "dokument";

export interface RabattInput {
  menge?: number | null;
  rabatt_prozent?: number | null;
  rabatt_betrag?: number | null;
  rabatt_modus?: string | null;
  rabatt_ausweis?: string | null;
}

export function normalizeRabattModus(value?: string | null): RabattModus {
  return value === "betrag" ? "betrag" : "prozent";
}

export function normalizeRabattAusweis(value?: string | null): RabattAusweis {
  return value === "dokument" ? "dokument" : "intern";
}

export function rabattIsActive(row: RabattInput): boolean {
  if (normalizeRabattModus(row.rabatt_modus) === "betrag") {
    return round2(Number(row.rabatt_betrag) || 0) > 0;
  }
  return (Number(row.rabatt_prozent) || 0) !== 0;
}

function lineNetBeforeTax(
  qty: number,
  unit: number,
  rabattProzent: number,
  rabattBetrag: number,
  rabattModus: RabattModus,
): { gross: number; net: number } {
  const grossLine = qty * unit;
  if (rabattModus === "betrag") {
    let amount = round2(Number.isFinite(rabattBetrag) ? rabattBetrag : 0);
    if (amount < 0) amount = 0;
    const cap = Math.abs(grossLine);
    if (amount > cap) amount = cap;
    const signed = grossLine >= 0 ? amount : -amount;
    return { gross: grossLine, net: round2(grossLine - signed) };
  }
  const discount = Number.isFinite(rabattProzent) ? rabattProzent : 0;
  const discountAmount = grossLine * (discount / 100);
  return { gross: grossLine, net: round2(grossLine - discountAmount) };
}

export function calcPosition(
  menge: number,
  einzelpreisNetto: number,
  rabattProzent = 0,
  steuersatzProzent = 19,
  options?: { rabattBetrag?: number; rabattModus?: RabattModus },
): PositionTotals {
  const qty = Number.isFinite(menge) ? menge : 1;
  const unit = round2(Number.isFinite(einzelpreisNetto) ? einzelpreisNetto : 0);
  const taxRate = Number.isFinite(steuersatzProzent) ? steuersatzProzent : 19;
  const { net } = lineNetBeforeTax(
    qty,
    unit,
    rabattProzent,
    Number(options?.rabattBetrag) || 0,
    options?.rabattModus ?? "prozent",
  );
  const tax = round2(net * (taxRate / 100));
  const gross = round2(net + tax);

  return { zeilen_netto: net, zeilen_steuer: tax, zeilen_brutto: gross };
}

const ZERO: PositionTotals = { zeilen_netto: 0, zeilen_steuer: 0, zeilen_brutto: 0 };

function parseKalkulation(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object") return {};
  return value as Record<string, unknown>;
}

export type KalkPreisbezug = "einzel" | "gesamt";

/** einzel = Betrag je Stück der Position, gesamt = einmal für die ganze Menge. */
export function normalizeKalkPreisbezug(value: unknown): KalkPreisbezug {
  return value === "gesamt" ? "gesamt" : "einzel";
}

/** Positionsmenge für die Umlegung von Gesamtpreisen. 0 wird zu 1. */
export function kalkPositionsMenge(value: unknown): number {
  const qty = Number(value);
  if (!Number.isFinite(qty) || qty === 0) return 1;
  return qty;
}

/** Anteil einer Kalkulationszeile am Einzelpreis der Leistung. */
export function betragAnteilEinzelpreis(
  betrag: number,
  preisbezug: unknown,
  positionsMenge: unknown,
): number {
  const amount = Number.isFinite(betrag) ? betrag : 0;
  if (normalizeKalkPreisbezug(preisbezug) !== "gesamt") return amount;
  return amount / kalkPositionsMenge(positionsMenge);
}

function calcArtikelListeNetto(artikelListe: unknown, positionsMenge: unknown): number {
  if (!Array.isArray(artikelListe)) return 0;
  return round2(
    artikelListe.reduce((sum, item) => {
      if (!item || typeof item !== "object") return sum;
      const row = item as Record<string, unknown>;
      const line = calcPosition(
        Number(row.menge ?? 1),
        Number(row.einzelpreis_netto ?? 0),
        Number(row.rabatt_prozent ?? 0),
        0,
      ).zeilen_netto;
      return sum + betragAnteilEinzelpreis(line, row.preisbezug, positionsMenge);
    }, 0),
  );
}

export function calcLeistungBaseNetto(kalkulationJson: unknown, positionsMenge: unknown = 1): number {
  const data = parseKalkulation(kalkulationJson);
  let subtotal = calcArtikelListeNetto(data.artikel_liste, positionsMenge);
  subtotal += calcLohnListeNetto(data, positionsMenge);
  subtotal = round2(subtotal);
  const aufschlag = Number(data.aufschlag_prozent ?? 0);
  if (aufschlag) subtotal = round2(subtotal * (1 + aufschlag / 100));
  return subtotal;
}

function resolveLeistungUnitNetto(
  kalkulationJson: unknown,
  einzelpreisNetto: number,
  positionsMenge: unknown,
): number {
  const data = parseKalkulation(kalkulationJson);
  const kalkuliert = calcLeistungBaseNetto(kalkulationJson, positionsMenge);
  const driver = data.vk_driver;
  const pauschale = Number(data.pauschale_netto ?? 0);
  if (driver === "pauschale" && pauschale > 0) return round2(pauschale);
  if (driver === "manuell" && einzelpreisNetto > 0) return round2(einzelpreisNetto);
  if (kalkuliert > 0) return kalkuliert;
  if (einzelpreisNetto > 0) return round2(einzelpreisNetto);
  return 0;
}

function calcLohnListeNetto(data: Record<string, unknown>, positionsMenge: unknown): number {
  const lohnListe = data.lohn_liste;
  if (Array.isArray(lohnListe) && lohnListe.length > 0) {
    return round2(
      lohnListe.reduce((sum, item) => {
        if (!item || typeof item !== "object") return sum;
        const row = item as Record<string, unknown>;
        const line = Number(row.stunden ?? 0) * Number(row.stundensatz_netto ?? 0);
        return sum + betragAnteilEinzelpreis(line, row.preisbezug, positionsMenge);
      }, 0),
    );
  }
  return round2(Number(data.stunden ?? 0) * Number(data.stundensatz_netto ?? 0));
}

function calcPositionFromRow(
  row: RabattInput & { menge?: number | null; steuersatz_prozent?: number | null },
  unit: number,
): PositionTotals {
  return calcPosition(
    Number(row.menge ?? 1),
    unit,
    Number(row.rabatt_prozent ?? 0),
    Number(row.steuersatz_prozent ?? 19),
    {
      rabattBetrag: Number(row.rabatt_betrag ?? 0),
      rabattModus: normalizeRabattModus(row.rabatt_modus),
    },
  );
}

export function calcRowForPosition(row: {
  position_typ?: string | null;
  menge?: number | null;
  einzelpreis_netto?: number | null;
  rabatt_prozent?: number | null;
  rabatt_betrag?: number | null;
  rabatt_modus?: string | null;
  rabatt_ausweis?: string | null;
  steuersatz_prozent?: number | null;
  kalkulation_json?: unknown;
}): PositionTotals {
  const typ = (row.position_typ || "artikel").toLowerCase();
  if (typ === "titel" || typ === "text") return ZERO;
  if (typ === "leistung") {
    const unit = resolveLeistungUnitNetto(
      row.kalkulation_json,
      Number(row.einzelpreis_netto ?? 0),
      row.menge,
    );
    return calcPositionFromRow(row, unit);
  }
  return calcPositionFromRow(row, Number(row.einzelpreis_netto ?? 0));
}

/** Listen-/Kalkulations-EP vor Positionsrabatt (bei Leistung inkl. Pauschale/Kalkulation). */
export function positionUnitNettoBeforeRabatt(row: {
  position_typ?: string | null;
  menge?: number | null;
  einzelpreis_netto?: number | null;
  kalkulation_json?: unknown;
}): number {
  const typ = (row.position_typ || "artikel").toLowerCase();
  if (typ === "titel" || typ === "text") return 0;
  if (typ === "leistung") {
    return resolveLeistungUnitNetto(
      row.kalkulation_json,
      Number(row.einzelpreis_netto ?? 0),
      row.menge,
    );
  }
  return Number.isFinite(Number(row.einzelpreis_netto)) ? Number(row.einzelpreis_netto) : 0;
}

/** EP nach Prozent-Rabatt je Einheit. */
export function applyRabattToUnit(einzelpreisNetto: number, rabattProzent = 0): number {
  const unit = round2(Number.isFinite(einzelpreisNetto) ? einzelpreisNetto : 0);
  const discount = Number.isFinite(rabattProzent) ? rabattProzent : 0;
  return round2(unit * (1 - discount / 100));
}

/** EP nach Rabatt. Euro wird auf die Menge verteilt. */
export function unitAfterRabatt(unit: number, menge: number, row: RabattInput): number {
  const rounded = round2(Number.isFinite(unit) ? unit : 0);
  if (normalizeRabattModus(row.rabatt_modus) !== "betrag") {
    return applyRabattToUnit(rounded, Number(row.rabatt_prozent) || 0);
  }
  const qty = Number.isFinite(menge) ? menge : 1;
  if (qty === 0) return rounded;
  const { net } = lineNetBeforeTax(
    qty,
    rounded,
    Number(row.rabatt_prozent) || 0,
    Number(row.rabatt_betrag) || 0,
    "betrag",
  );
  return round2(net / qty);
}

/** EP, den der Kunde sieht. */
export function unitForCustomer(unit: number, menge: number, row: RabattInput): number {
  const rounded = round2(Number.isFinite(unit) ? unit : 0);
  if (normalizeRabattAusweis(row.rabatt_ausweis) === "dokument" && rabattIsActive(row)) {
    return rounded;
  }
  return unitAfterRabatt(rounded, menge, row);
}

export function positionEpNetto(row: {
  position_typ?: string | null;
  menge?: number | null;
  einzelpreis_netto?: number | null;
  rabatt_prozent?: number | null;
  rabatt_betrag?: number | null;
  rabatt_modus?: string | null;
  rabatt_ausweis?: string | null;
  steuersatz_prozent?: number | null;
  kalkulation_json?: unknown;
}): number {
  return unitForCustomer(
    positionUnitNettoBeforeRabatt(row),
    Number(row.menge ?? 1),
    row,
  );
}

export function formatRabattPercent(value: number): string {
  if (!Number.isFinite(value)) return "0";
  const rounded = Math.round(value * 100) / 100;
  if (Math.abs(rounded - Math.round(rounded)) < 1e-9) return String(Math.round(rounded));
  return rounded.toFixed(2).replace(/\.?0+$/, "").replace(".", ",");
}

/** Rabattzeile auf dem Kundendokument, z. B. „Rabatt 10 % (12,00 €)“. */
export function documentRabattLabel(
  row: {
    position_typ?: string | null;
    menge?: number | null;
    einzelpreis_netto?: number | null;
    rabatt_prozent?: number | null;
    rabatt_betrag?: number | null;
    rabatt_modus?: string | null;
    rabatt_ausweis?: string | null;
    steuersatz_prozent?: number | null;
    kalkulation_json?: unknown;
  },
  currency = "EUR",
): string | null {
  if (normalizeRabattAusweis(row.rabatt_ausweis) !== "dokument" || !rabattIsActive(row)) {
    return null;
  }
  const unit = round2(positionUnitNettoBeforeRabatt(row));
  const qty = Number.isFinite(Number(row.menge)) ? Number(row.menge) : 1;
  const modus = normalizeRabattModus(row.rabatt_modus);
  const { gross, net } = lineNetBeforeTax(
    qty,
    unit,
    Number(row.rabatt_prozent) || 0,
    Number(row.rabatt_betrag) || 0,
    modus,
  );
  const amount = round2(gross - net);
  if (modus === "betrag") return `Rabatt ${formatCurrency(amount, currency)}`;
  const pct = formatRabattPercent(Number(row.rabatt_prozent) || 0);
  if (amount !== 0) return `Rabatt ${pct} % (${formatCurrency(amount, currency)})`;
  return `Rabatt ${pct} %`;
}

/** Kurzhinweis in der Positionsliste (Büro). */
export function rabattEditorHint(row: RabattInput, currency = "EUR"): string | null {
  if (!rabattIsActive(row)) return null;
  const modus = normalizeRabattModus(row.rabatt_modus);
  const where = normalizeRabattAusweis(row.rabatt_ausweis) === "dokument" ? "auf Rechnung" : "nur intern";
  const value =
    modus === "betrag"
      ? formatCurrency(Number(row.rabatt_betrag) || 0, currency)
      : `${formatRabattPercent(Number(row.rabatt_prozent) || 0)} %`;
  return `Rabatt ${value} · ${where}`;
}

/** Alternative Angebotspositionen zählen nicht zur Dokumentensumme (Optional schon). */
export function isExcludedFromDocumentTotal(row: {
  kalkulation_json?: unknown;
}): boolean {
  const kalk = row.kalkulation_json;
  if (!kalk || typeof kalk !== "object") return false;
  const meta = (kalk as Record<string, unknown>)._gd_meta;
  if (!meta || typeof meta !== "object") return false;
  const art = String((meta as Record<string, unknown>).positionsart || "").toLowerCase();
  return art === "alternativ";
}

export function calcDocument(rows: Array<{
  position_typ?: string | null;
  menge?: number | null;
  einzelpreis_netto?: number | null;
  rabatt_prozent?: number | null;
  steuersatz_prozent?: number | null;
  kalkulation_json?: unknown;
}>): DocumentTotals {
  const groups = groupTaxByRate(rows);
  const net = round2(groups.reduce((s, g) => s + g.net, 0));
  const tax = round2(groups.reduce((s, g) => s + g.tax, 0));
  return {
    summe_netto: net,
    summe_steuer: tax,
    summe_brutto: round2(net + tax),
  };
}

export function formatCurrency(value: number | null | undefined, currency = "EUR"): string {
  const num = Number(value ?? 0);
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
  }).format(num);
}

function normalizeQuantityNumber(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(Number(value))) return null;
  return Math.round(Number(value) * 10000) / 10000;
}

/** Anzeige: ganze Mengen ohne Nachkommastellen, sonst nur nötige Dezimalstellen. */
export function formatQuantity(value: number | null | undefined): string {
  const num = normalizeQuantityNumber(value);
  if (num == null) return "";
  if (Math.abs(num - Math.round(num)) < 1e-9) {
    return String(Math.round(num));
  }
  return num.toFixed(4).replace(/\.?0+$/, "").replace(".", ",");
}

/** Für number-Inputs: Punkt als Dezimaltrenner, keine überflüssigen Nullen. */
export function formatQuantityInput(value: number | null | undefined): string {
  const num = normalizeQuantityNumber(value);
  if (num == null) return "";
  if (Math.abs(num - Math.round(num)) < 1e-9) {
    return String(Math.round(num));
  }
  return num.toFixed(4).replace(/\.?0+$/, "");
}

export function formatQuantityWithUnit(
  menge: number | null | undefined,
  einheit?: string | null,
): string {
  const qty = formatQuantity(menge);
  const unit = (einheit || "").trim();
  return unit ? `${qty} ${unit}` : qty;
}

export function groupTaxByRate(
  rows: Array<{
    position_typ?: string | null;
    menge?: number | null;
    einzelpreis_netto?: number | null;
    rabatt_prozent?: number | null;
    steuersatz_prozent?: number | null;
    kalkulation_json?: unknown;
  }>,
): Array<{ rate: number; net: number; tax: number }> {
  const map = new Map<number, { net: number }>();
  for (const row of rows) {
    const typ = (row.position_typ || "artikel").toLowerCase();
    if (typ === "titel" || typ === "text") continue;
    if (isExcludedFromDocumentTotal(row)) continue;
    const rate = Number(row.steuersatz_prozent ?? 19);
    const line = calcRowForPosition(row);
    const prev = map.get(rate) ?? { net: 0 };
    map.set(rate, { net: round2(prev.net + line.zeilen_netto) });
  }
  return [...map.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([rate, values]) => ({
      rate,
      net: values.net,
      tax: round2(values.net * (rate / 100)),
    }));
}
